import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "../ui/dialog";
import { Button } from "../ui/button";
import { UserCog, Search, Loader2, X, Check } from "lucide-react";
import { cn } from "../ui/utils";
import api from "../../utils/api";

import { getRandomAvatarColor, getUserInitials } from "../../utils/avatar-utils";

interface User {
  id: string;
  name: string;
  email: string;
  initials: string;
  color: string;
  permissions?: string[];
  userRole?: string;
  archived?: boolean;
}

interface MemberSelectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (user: User) => void;
  deskTitle: string;
  title?: string;
  stepDescription?: string;
  selectedIds?: string[];
  excludeIds?: string[];
  requiredPermission?: string;
  filterUser?: (user: User) => boolean;
}

const MemberSelectModal: React.FC<MemberSelectModalProps> = ({ 
  isOpen, 
  onClose, 
  onAdd,
  deskTitle,
  title = "Add Team Member",
  stepDescription,
  selectedIds = [],
  excludeIds = [],
  requiredPermission,
  filterUser
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchUsers("");
    } else {
      setSearchQuery("");
      setUsers([]);
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    }
  }, [isOpen]);

  const fetchUsers = async (query = "") => {
    // If we already have users and it's a quick search, don't show the massive central loader
    // to prevent flickering. Only show it on initial load or if query is cleared.
    const showFullLoader = users.length === 0;
    if (showFullLoader) setIsLoading(true);

    try {
      const url = `/api/users/selection?search=${query}${requiredPermission ? `&permission=${requiredPermission}` : ''}`;
      const response = await api.get(url);
      if (response.success) {
        const mappedUsers = response.users.map((u: any) => ({
          id: String(u.id),
          name: u.name,
          email: u.email,
          initials: getUserInitials(u.name),
          color: getRandomAvatarColor(u.name),
          permissions: u.permissions || [],
          userRole: u.userRole,
          archived: u.archived
        }));
        setUsers(mappedUsers);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    
    searchTimeoutRef.current = setTimeout(() => {
        fetchUsers(value);
    }, 500);
  };

  // Convert all selected/excluded IDs to strings for robust comparison
  const normalizedSelectedIds = selectedIds.map(id => String(id));
  const normalizedExcludeIds = excludeIds.map(id => String(id));

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose={true} className="p-0 sm:max-w-md border-none overflow-hidden bg-white shadow-xl">
        <DialogHeader className="p-6 border-b border-border flex-row items-center justify-between space-y-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
                <UserCog className="w-5 h-5 text-primary" />
            </div>
            <DialogTitle className="text-xl font-bold">{title}</DialogTitle>
          </div>
          <button 
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        <div className="p-6">
          <DialogDescription className="text-sm text-muted-foreground mb-4 font-medium">
            {stepDescription || `Select a user to add to ${deskTitle}`}
          </DialogDescription>

          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input 
              type="text" 
              placeholder="Search users..." 
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-10 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 bg-background"
            />
            {isLoading && users.length > 0 && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
            )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {isLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : users.filter(user => !user.archived && !normalizedExcludeIds.includes(user.id) && (!filterUser || filterUser(user))).length > 0 ? (
              users
                .filter(user => !user.archived && !normalizedExcludeIds.includes(user.id) && (!filterUser || filterUser(user)))
                .map((user) => {
                  const isSelected = normalizedSelectedIds.includes(String(user.id));
                  return (
                    <button 
                      key={user.id}
                      onClick={() => onAdd(user)}
                      className={cn(
                        "w-full flex items-center gap-3 p-3 bg-card border rounded-lg transition-colors group",
                        isSelected ? "bg-primary/5 border-primary shadow-sm" : "border-border hover:bg-accent"
                      )}
                    >
                      <div className={cn(
                        "w-8 h-8 text-xs rounded-full flex items-center justify-center text-white font-medium shadow-sm",
                        user.color
                      )}>
                        {user.initials}
                      </div>
                      <div className="flex-1 text-left">
                        <div className={cn(
                          "font-medium group-hover:text-primary transition-colors",
                          isSelected && "text-primary"
                        )}>{user.name}</div>
                        <div className="text-xs text-muted-foreground">{user.email}</div>
                      </div>
                      {isSelected && <Check className="w-5 h-5 text-primary shrink-0" />}
                    </button>
                  );
                })
            ) : (
              <div className="text-center p-8 text-sm text-muted-foreground font-medium">
                {searchQuery ? "No users found matching your search." : "No eligible users found."}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="p-6 border-t border-border bg-muted/30">
          <Button variant="outline" onClick={onClose} className="w-full sm:w-auto">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MemberSelectModal;
